import Vue from 'vue';
import _ from 'lodash';
import moment from 'moment';
import { EnrollmentType } from '@trialcard/enums';
import { Validation } from 'vuelidate';
import { IFieldBase } from '~/types/ruleTypes';
import { Settings } from '~/services/settings';


export default class CommonTools {
    /**
     * Derives the patient-search display values (Member Number, Group Number, Status) from the
     * getBestEnrollmentV2 result for a given account. Among the enrollments matching the account,
     * one flagged enrollmentFlags.isActiveEnrollmentForCurrentDate is the source of truth; if none
     * are flagged, the enrollment with the latest endDate is used instead. When no enrollment
     * matches the account, all three return '-'.
     */
    static getSearchEnrollmentDisplay(enrollments: any[] | undefined | null, accountId: any) {
        const dash = '-';
        const matches = (enrollments || []).filter((e: any) => e && e.accountId === accountId);
        if (matches.length === 0) {
            return { memberNumber: dash, groupNumber: dash, status: dash };
        }

        const currentlyActive = matches.filter(
            (e: any) => e && e.enrollmentFlags && e.enrollmentFlags.isActiveEnrollmentForCurrentDate === true
        );
        const candidates = currentlyActive.length > 0 ? currentlyActive : matches;
        const chosen = _.orderBy(candidates, (e: any) => moment(e.endDate).valueOf(), 'desc')[0];
        const pharmacyMemberNumber = (chosen.memberNumbers || []).find((m: any) => m && m.type === EnrollmentType.Pharmacy);
        const memberNumber = (pharmacyMemberNumber && pharmacyMemberNumber.number) || dash;
        // copayProgramType: 1 = base, 2 = accumulator, null/undefined = base default. Maximizer (3) is not used by this program.
        const groupNumber = chosen.copayProgramType === 2 ? '77770230' : '77770205';
        const status = moment(chosen.endDate).isAfter(moment.utc()) ? 'Active' : 'Deactivated';

        return { memberNumber, groupNumber, status };
    }
    static titleCase(str: string) {
        return str
            .toLowerCase()
            .split(' ')
            .map(function(word) {
                return (
                    word
                        .toLocaleLowerCase()
                        .charAt(0)
                        .toLocaleUpperCase() + word.slice(1)
                );
            })
            .join(' ');
    }

    static sentenceCase(str: string) {
        const sentence = str
            .toLowerCase()
            .split(' ')
            .map(function(word) {
                return word.toLocaleLowerCase() + word.slice(1);
            })
            .join(' ');

        return sentence.charAt(0).toLocaleUpperCase() + sentence.slice(1);
    }

    static titleFormat(str: string) {
        return ((str.charAt(0).toLocaleUpperCase() + str.slice(1)).match(/[A-Z][a-z]+/g) || []).join(' ');
    }

    // Only define functions, looping thru attributes
    static fieldKeys(searchFields: IFieldBase) {
        const searchKeys = Object.getOwnPropertyNames(searchFields.getFieldDefaults());
        const parentKeys = Object.getOwnPropertyNames(new Vue());
        const returnKeys = _.difference(searchKeys, parentKeys);

        return returnKeys;
    }

    static pascalCase(str: string) {
        return _.startCase(_.camelCase(str || '')).replace(/ /g, '');
    }

    static safeToString(item: string) {
        return (item || '').toString();
    }

    static safeToLowerCase(item: string) {
        return (item || '').toLocaleLowerCase();
    }

    static safeToStringLowerCase(item: string) {
        return this.safeToLowerCase(this.safeToString(this.safeStringTrim(item)));
    }

    static safeStringTrim(item: string) {
        return (item || '').trim();
    }

    static stringToCurrencyFormat(value: string) {
        return this.toCurrencyFormat(Number.parseFloat(value));
    }

    static toCurrencyFormat(number: number) {
        return Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(number);
    }

    static phoneToString(item: string) {
        return (item || '').replace(/[^A-Z0-9]/gi, '');
    }

    static stringToPhone(item: string) {
        return (item || '').replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
    }

    static auth0StringToPhone(item: string) {
        return this.stringToPhone((item || '').replace(/\+1/, ''));
    }

    // Add to any component with a vuelidate validator and this will log any validation errors
    // to the console.  This is used to diagnose situations where validation fails silently
    static checkError(validator: Validation) {
        const errorKeys = Object.keys(validator).filter((key) => !key.startsWith('$') && validator[key as keyof typeof validator].$anyError);
        errorKeys.length > 0 && console.error('Error on properties:', errorKeys.join(', '));
    }

    // This function works by checking the position of the current page in the pages array
    // and then looking for the next or previous page in the array based on the 'goBack' argument
    static getRouteName(currentPage: string, pages: string[], goBack = false, pathStart = 'need-a-card-'): string {
        const index = pages.indexOf(currentPage);
        let nextPage = ''
        if (goBack && index === 0) {
            return 'index';
        } else if(goBack && index >= 0 && index <= pages.length - 1) {
            nextPage = pages[index - 1]
        } else if (index >= 0 && index < pages.length - 1) {
            nextPage = pages[index + 1]
        } else {
            return pathStart + pages[0]
        }
        return pathStart + nextPage;
    }
    static getBaseUrl(url: string, settings: Settings) {
        const urlMap = {
            localhost: 'http://localhost:3000/',
            qa: 'https://portal-qa.trialcard.com/madrigal/rezdiffra/',
            uat: 'https://portal-uat.trialcard.com/madrigal/rezdiffra/',
            preProd: 'https://portal-prod-int.trialcard.com/madrigal/rezdiffra/',
            prod: 'https://portal.trialcard.com/madrigal/rezdiffra/',
        }
        let baseUrl = urlMap.localhost;
        if (url.includes('prod-int')) {
            baseUrl = urlMap.preProd
        }
        // else if (url.includes('journavxpap')) {
        //     const vanityUrl = new URL(url)
        //     baseUrl = `${vanityUrl.origin}/`
        //     console.log('baseUrl :', baseUrl);
        // }
        else if (['qa', 'uat', 'prod'].includes(settings.ConnectTo) && !url.includes('localhost')) {
            baseUrl = urlMap[settings.ConnectTo as 'qa' | 'uat' | 'prod']
        }
        return baseUrl
    }
}
